import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { VStack } from "@design-system/VStack/VStack";
import { LeftArrowIcon } from "@icons";
import {
    ScheduleLinkedConflictOption,
    ScheduleLinkedConflictView,
} from "./scheduleLinkedConflictCopy";
import styles from "./ScheduleLinkedConflictModal.module.scss";

interface ScheduleLinkedConflictModalProps {
    show: boolean;
    view: ScheduleLinkedConflictView;
    onConfirm: (option: ScheduleLinkedConflictOption) => Promise<void>;
    onBack: () => void;
    onExited: () => void;
}

/**
 * A payment and the invoice it pays would both carry a schedule. The user
 * keeps one (the other is removed with its journal entries) or, when applying,
 * unmatches the two instead.
 */
export const ScheduleLinkedConflictModal = ({
    show,
    view,
    onConfirm,
    onBack,
    onExited,
}: ScheduleLinkedConflictModalProps) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selected =
        view.options.find((option) => option.id === selectedId) ?? null;

    const handleConfirm = async () => {
        if (selected) {
            await onConfirm(selected);
        }
    };

    return (
        <PlainModal show={show} onHide={onBack} onExited={onExited}>
            <StandardModalHeader>{view.title}</StandardModalHeader>
            <VStack gap={24}>
                <p className={styles.body}>{view.body}</p>
                <RadioGroup
                    aria-label={view.title}
                    value={selectedId}
                    onValueChange={setSelectedId}
                    disabled={isSubmitting}
                    className={styles.options}
                >
                    {view.options.map((option) => (
                        <Radio
                            key={option.id}
                            value={option.id}
                            className={styles.option}
                            label={
                                <span className={styles.optionCopy}>
                                    <span>{option.title}</span>
                                    <span className={styles.optionDescription}>
                                        {option.description}
                                    </span>
                                </span>
                            }
                        />
                    ))}
                </RadioGroup>
                <div className={styles.footer}>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={onBack}
                        disabled={isSubmitting}
                        iconOnly
                        size="xl"
                        aria-label="Go back"
                    >
                        <LeftArrowIcon />
                    </Button>
                    <ButtonWithLoader
                        variant="primary"
                        type="button"
                        size="xl"
                        disabled={!selected}
                        loading={isSubmitting}
                        setLoading={setIsSubmitting}
                        onClick={handleConfirm}
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            enabled: show && selected !== null,
                            description: "Confirm",
                            preventDefault: true,
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        Confirm
                    </ButtonWithLoader>
                </div>
            </VStack>
        </PlainModal>
    );
};
